import { useEffect, useRef } from 'react';
import { Box, GlobalStyles } from '@mui/material';

declare global {
  interface Window {
    ClassicEditor?: any;
  }
}

const CDN_URL = 'https://cdn.ckeditor.com/ckeditor5/41.1.0/classic/ckeditor.js';

function loadCkeditor(): Promise<any> {
  if (window.ClassicEditor) return Promise.resolve(window.ClassicEditor);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${CDN_URL}"]`) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(window.ClassicEditor));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = CDN_URL;
    script.async = true;
    script.onload = () => resolve(window.ClassicEditor);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function CkEditor({ html, onChange }: { html: string; onChange: (v: string) => void }) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<any>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const ClassicEditor = await loadCkeditor();
      if (!mounted || !hostRef.current) return;
      editorRef.current = await ClassicEditor.create(hostRef.current, {
        toolbar: [
          'bold', 'italic', '|', 'link', 'unlink', '|', 'bulletedList', 'numberedList', '|', 'undo', 'redo'
        ],
        placeholder: 'テキストを入力...',
        link: {
          addTargetToExternalLinks: true,
          decorators: {
            openInNewTab: {
              mode: 'manual',
              label: '新しいタブで開く',
              attributes: {
                target: '_blank',
                rel: 'noopener noreferrer'
              }
            }
          }
        },
        ui: {
          viewportOffset: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0
          }
        }
      });

      // CKEditor内でのドラッグイベントを文字列選択として処理
      if (hostRef.current) {
        hostRef.current.addEventListener('dragstart', (e) => e.stopPropagation());
        hostRef.current.addEventListener('dragover', (e) => e.stopPropagation());
        hostRef.current.addEventListener('drop', (e) => e.stopPropagation());
      }
      editorRef.current.setData(html || '');
      editorRef.current.model.document.on('change:data', () => {
        const data: string = editorRef.current.getData();
        onChange(data);
      });
    })();
    return () => {
      mounted = false;
      if (editorRef.current) {
        editorRef.current.destroy().catch(() => void 0);
        editorRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // 外部から値が変わった場合のみ同期（編集中の上書きは避けるため簡易判定）
    if (editorRef.current) {
      const current = editorRef.current.getData();
      if (current !== html) editorRef.current.setData(html || '');
    }
  }, [html]);

  return (
    <Box>
      <GlobalStyles styles={{
        ':root': {
          '--ck-z-default': '100',
          '--ck-z-panel': 'calc(var(--ck-z-default) + 999)',
        },
        '.ck-body-wrapper': { zIndex: 'var(--ck-z-panel)' },
        '.ck-balloon-panel, .ck.ck-balloon-panel': { zIndex: 'var(--ck-z-panel)' },
        '.ck.ck-dropdown__panel, .ck-editor__top': { zIndex: 'var(--ck-z-panel)' },
        '.ck-tooltip': { zIndex: 'var(--ck-z-panel)' },
      }} />
      <Box 
        sx={{ 
          border: '1px solid #ddd', 
          borderRadius: 1, 
          bgcolor: '#fff', 
          minHeight: 80, 
          p: 1,
          userSelect: 'text'
        }}
        draggable={false}
        onDragStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div 
          ref={hostRef}
          draggable={false}
          onDragStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        />
      </Box>
    </Box>
  );
}


